import { getFirestore, collection, addDoc, getDocs, query, orderBy, doc, updateDoc, arrayUnion, onSnapshot, Timestamp, setDoc } from 'firebase/firestore'
import { app } from './firebase'

const db = getFirestore(app)

// Post interface
export interface Post {
  id: string
  content: string
  authorName: string
  authorId: string
  createdAt: Timestamp
  replies: Reply[]
  symbol: string
  ltp: number
}

// Reply interface
export interface Reply {
  id: string
  content: string
  authorName: string
  authorId: string
  createdAt: Timestamp
}

// Symbol interface for validated symbols
export interface ValidatedSymbol {
  symbol: string
  ltp: number
  lastValidated: Timestamp
  broker: string
  exchange?: string
}

// Create a new post in a symbol-specific subcollection
export const createPost = async (content: string, authorName: string, authorId: string, symbol: string, ltp: number) => {
  try {
    // Add the symbol and LTP tag to the content
    const contentWithTag = `${content} \n[#${symbol}:${ltp}]`
    
    // Create the post in the symbol-specific subcollection
    const docRef = await addDoc(collection(db, 'posts', symbol, 'posts'), {
      content: contentWithTag,
      authorName,
      authorId,
      createdAt: Timestamp.now(),
      replies: [],
      symbol,
      ltp
    })
    
    // Update the symbol metadata if this is the first post for this symbol
    await updateSymbolMetadata(symbol, ltp)
    
    return docRef.id
  } catch (error) {
    console.error('Error creating post:', error)
    throw error
  }
}

// Update symbol metadata when a post is created
const updateSymbolMetadata = async (symbol: string, ltp: number) => {
  try {
    const symbolDocRef = doc(db, 'symbols', symbol)
    await setDoc(symbolDocRef, {
      symbol,
      ltp,
      lastUpdated: Timestamp.now(),
      postCount: 1 // This could be incremented in a transaction
    }, { merge: true })
  } catch (error) {
    console.error('Error updating symbol metadata:', error)
    // Don't throw here as the post creation should succeed even if metadata update fails
  }
}

// Add a reply to a post in a symbol-specific subcollection
export const addReply = async (symbol: string, postId: string, content: string, authorName: string, authorId: string) => {
  try {
    const postRef = doc(db, 'posts', symbol, 'posts', postId)
    const reply: Reply = {
      id: Date.now().toString(),
      content,
      authorName,
      authorId,
      createdAt: Timestamp.now()
    }
    await updateDoc(postRef, {
      replies: arrayUnion(reply)
    })
    return reply.id
  } catch (error) {
    console.error('Error adding reply:', error)
    throw error
  }
}

// Get posts for a specific symbol (real-time)
export const subscribeToPostsBySymbol = (symbol: string, callback: (posts: Post[]) => void) => {
  const q = query(collection(db, 'posts', symbol, 'posts'), orderBy('createdAt', 'desc'))
  
  return onSnapshot(q, (querySnapshot) => {
    const posts: Post[] = []
    querySnapshot.forEach((doc) => {
      posts.push({
        id: doc.id,
        ...doc.data()
      } as Post)
    })
    callback(posts)
  })
}

// Get posts for a specific symbol (one-time fetch)
export const fetchPostsBySymbol = async (symbol: string): Promise<Post[]> => {
  try {
    const q = query(collection(db, 'posts', symbol, 'posts'), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    const posts: Post[] = []
    querySnapshot.forEach((doc) => {
      posts.push({
        id: doc.id,
        ...doc.data()
      } as Post)
    })
    return posts
  } catch (error) {
    console.error('Error fetching posts:', error)
    throw error
  }
}

// Get available symbols that have posts
export const getAvailableSymbols = async (): Promise<string[]> => {
  try {
    const symbolsSnapshot = await getDocs(collection(db, 'symbols'))
    const symbols: string[] = []
    symbolsSnapshot.forEach((doc) => {
      symbols.push(doc.id)
    })
    return symbols.sort()
  } catch (error) {
    console.error('Error fetching symbols:', error)
    return []
  }
}

// Directory Contact interfaces
export interface BankDetails {
  // Common fields
  accountHolderName: string
  accountNumber: string
  bankName: string

  // Indian bank specific fields
  ifscCode?: string
  branchName?: string
  branchAddress?: string
  accountType?: string
  panNumber?: string
  aadharNumber?: string
  mobileNumber?: string
  emailAddress?: string

  // International bank specific fields
  swiftCode?: string
  bankAddress?: string
  routingNumber?: string
  sortCode?: string
  bsb?: string
  transitNumber?: string
  country?: string
  currency?: string
  correspondentBank?: string
  correspondentSwift?: string
  purpose?: string
}

export interface DirectoryContact {
  id: string
  title: string
  type: 'nintag' | 'strike_username' | 'onchain_address' | 'indian_bank' | 'international_bank' | 'upi'
  value: string | BankDetails
  network?: string // Network for onchain addresses (e.g., 'bitcoin', 'ethereum', 'lightning')
  notes: string
  userId: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

// Directory CRUD operations

// Create a new contact
export const createDirectoryContact = async (
  title: string,
  type: DirectoryContact['type'],
  value: string | BankDetails,
  notes: string,
  userId: string,
  network?: string
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'directory-contacts'), {
      title,
      type,
      value,
      notes,
      userId,
      network,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating directory contact:', error)
    throw error
  }
}

// Get all contacts for a user
export const getDirectoryContacts = async (userId: string): Promise<DirectoryContact[]> => {
  try {
    const q = query(
      collection(db, 'directory-contacts'),
      orderBy('createdAt', 'desc')
    )

    const querySnapshot = await getDocs(q)
    const contacts: DirectoryContact[] = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      if (data.userId === userId) {
        contacts.push({
          id: doc.id,
          ...data
        } as DirectoryContact)
      }
    })

    return contacts
  } catch (error) {
    console.error('Error fetching directory contacts:', error)
    throw error
  }
}

// Subscribe to real-time updates for user's contacts
export const subscribeToDirectoryContacts = (userId: string, callback: (contacts: DirectoryContact[]) => void) => {
  const q = query(
    collection(db, 'directory-contacts'),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(q, (querySnapshot) => {
    const contacts: DirectoryContact[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      if (data.userId === userId) {
        contacts.push({
          id: doc.id,
          ...data
        } as DirectoryContact)
      }
    })
    callback(contacts)
  })
}

// Update a contact
export const updateDirectoryContact = async (
  contactId: string,
  updates: Partial<Omit<DirectoryContact, 'id' | 'userId' | 'createdAt'>>
): Promise<void> => {
  try {
    const contactRef = doc(db, 'directory-contacts', contactId)
    await updateDoc(contactRef, {
      ...updates,
      updatedAt: Timestamp.now()
    })
  } catch (error) {
    console.error('Error updating directory contact:', error)
    throw error
  }
}

// Delete a contact
export const deleteDirectoryContact = async (contactId: string): Promise<void> => {
  try {
    const contactRef = doc(db, 'directory-contacts', contactId)
    await updateDoc(contactRef, {
      updatedAt: Timestamp.now(),
      // Soft delete by marking as deleted
      deleted: true
    })
  } catch (error) {
    console.error('Error deleting directory contact:', error)
    throw error
  }
}

// Get a specific contact by ID
export const getDirectoryContactById = async (contactId: string, userId: string): Promise<DirectoryContact | null> => {
  try {
    const contactRef = doc(db, 'directory-contacts', contactId)
    const contactSnap = await getDocs(query(collection(db, 'directory-contacts'), orderBy('createdAt', 'desc')))

    let contact: DirectoryContact | null = null
    contactSnap.forEach((doc) => {
      const data = doc.data()
      if (doc.id === contactId && data.userId === userId) {
        contact = {
          id: doc.id,
          ...data
        } as DirectoryContact
      }
    })

    return contact
  } catch (error) {
    console.error('Error fetching directory contact:', error)
    throw error
  }
}

// Validate symbol and get LTP
export const validateSymbol = async (symbol: string): Promise<{ isValid: boolean; ltp?: number; error?: string }> => {
  try {
    // Client-side validation using ConnectorManager
    if (typeof window === 'undefined') {
      return { isValid: false, error: 'Validation only available on client side' }
    }

    try {
      const { ConnectorManager } = await import('../components/Connectors/ConnectorManager')
      const connectorManager = ConnectorManager.getInstance()
      const brokers = connectorManager.getBrokers()
      const enabledBroker = brokers.find(broker => broker.enabled && broker.isConnected)

      if (!enabledBroker) {
        return {
          isValid: false,
          error: 'No broker is enabled or connected. Please connect a broker first.'
        }
      }

      const connector = connectorManager.getConnector(enabledBroker.id)
      if (!connector) {
        return {
          isValid: false,
          error: 'Broker connector not found'
        }
      }

      console.log('connector.getLTP', connector.getLTP)

      // Validate symbol by fetching LTP
      let ltp: number | null = null

      try {
        // Different brokers have different LTP methods
        if ('getLTP' in connector && typeof connector.getLTP === 'function') {
          const ltpData = await (connector as any).getLTP([symbol])

          // Parse LTP based on broker format
          if (enabledBroker.id === 'zerodha' || enabledBroker.id === 'groww') {
            // Zerodha/Groww format: { "NSE:SYMBOL": { "last_price": 123.45 } }
            const symbolKey = Object.keys(ltpData)[0]
            ltp = symbolKey ? ltpData[symbolKey]?.last_price || ltpData[symbolKey]?.ltp : null
          } else if (enabledBroker.id === 'binance') {
            // Binance format: { "BTCUSDT": { "last_price": "50000.00" } }
            const symbolKey = Object.keys(ltpData)[0]
            ltp = symbolKey ? ltpData[symbolKey]?.last_price || ltpData[symbolKey]?.ltp : null
          } else if (enabledBroker.id === 'nintrade') {
            // Nintrade format - similar to Zerodha
            const symbolKey = Object.keys(ltpData)[0]
            ltp = symbolKey ? ltpData[symbolKey]?.last_price || ltpData[symbolKey]?.ltp : null
          }
        } else if ('getTickers' in connector && typeof connector.getTickers === 'function') {
          // Fallback to getTickers if getLTP is not available
          const tickerData = await (connector as any).getTickers({ symbol })
          ltp = tickerData && tickerData.length > 0 ? tickerData[0].price : null
        }
      } catch (error) {
        console.error('Error fetching LTP:', error)
        return {
          isValid: false,
          error: 'Failed to fetch LTP for symbol. Symbol may not exist or broker may be unavailable.'
        }
      }

      if (ltp === null || isNaN(ltp) || ltp <= 0) {
        return {
          isValid: false,
          error: 'Invalid symbol or Wrong connector'
        }
      }

      return { isValid: true, ltp }

    } catch (error) {
      console.error('Error accessing ConnectorManager:', error)
      return {
        isValid: false,
        error: 'Failed to access broker connector'
      }
    }
  } catch (error) {
    console.error('Error validating symbol:', error)
    return { isValid: false, error: 'Network error while validating symbol' }
  }
}